import {
  Antenna,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Boxes,
  Building2,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Cookie,
  Factory,
  Globe2,
  Headphones,
  HeartHandshake,
  Mail,
  Map as MapIcon,
  MapPin,
  Network,
  Phone,
  Pickaxe,
  Play,
  Radio,
  RadioTower,
  SatelliteDish,
  Send,
  Server,
  ShieldCheck,
  ShieldPlus,
  Siren,
  Smartphone,
  UsersRound,
  Wrench,
  type LucideIconData,
} from "@lucide/icons";
import { buildLucideIconElement } from "@lucide/icons/build";

const icons: Record<string, LucideIconData> = {
  antenna: Antenna,
  "arrow-left": ArrowLeft,
  "arrow-right": ArrowRight,
  "arrow-up": ArrowUp,
  boxes: Boxes,
  building: Building2,
  calendar: CalendarDays,
  "chevron-down": ChevronDown,
  "chevron-left": ChevronLeft,
  "chevron-right": ChevronRight,
  cookie: Cookie,
  facebook: Globe2,
  factory: Factory,
  globe: Globe2,
  headphones: Headphones,
  heart: HeartHandshake,
  linkedin: UsersRound,
  mail: Mail,
  map: MapIcon,
  "map-pin": MapPin,
  network: Network,
  phone: Phone,
  pickaxe: Pickaxe,
  play: Play,
  radio: Radio,
  "radio-tower": RadioTower,
  satellite: SatelliteDish,
  send: Send,
  server: Server,
  shield: ShieldCheck,
  "shield-plus": ShieldPlus,
  siren: Siren,
  smartphone: Smartphone,
  users: UsersRound,
  wrench: Wrench,
  youtube: Play,
};

function renderIcons(): void {
  document
    .querySelectorAll<HTMLElement>("[data-icon]")
    .forEach((placeholder) => {
      const icon = icons[placeholder.dataset.icon ?? ""];
      if (!icon) return;

      const svg = buildLucideIconElement(document, icon, {
        color: "currentColor",
        strokeWidth: 1.7,
        absoluteStrokeWidth: true,
        attributes: { "aria-hidden": "true", focusable: "false" },
      });

      placeholder.classList.forEach((className) =>
        svg.classList.add(className),
      );
      placeholder.replaceWith(svg);
    });
}

function initMenu(): void {
  const toggle =
    document.querySelector<HTMLButtonElement>("[data-menu-toggle]");
  const menu = document.querySelector<HTMLElement>("[data-menu]");
  if (!toggle || !menu) return;

  toggle.addEventListener("click", () => {
    const isOpen = toggle.getAttribute("aria-expanded") === "true";
    toggle.setAttribute("aria-expanded", String(!isOpen));
    toggle.setAttribute(
      "aria-label",
      isOpen ? "Ouvrir le menu" : "Fermer le menu",
    );
    menu.classList.toggle("open", !isOpen);
  });

  menu.addEventListener("click", (event) => {
    if (!(event.target as HTMLElement).closest("a")) return;
    toggle.setAttribute("aria-expanded", "false");
    toggle.setAttribute("aria-label", "Ouvrir le menu");
    menu.classList.remove("open");
  });
}

function initNavDropdown(): void {
  document.querySelectorAll<HTMLElement>("[data-nav-dropdown]").forEach((dropdown) => {
    const toggle = dropdown.querySelector<HTMLButtonElement>("[data-nav-dropdown-toggle]");
    if (!toggle) return;

    const close = (): void => {
      dropdown.classList.remove("is-open");
      toggle.setAttribute("aria-expanded", "false");
    };

    toggle.addEventListener("click", (event) => {
      event.stopPropagation();
      const isOpen = dropdown.classList.toggle("is-open");
      toggle.setAttribute("aria-expanded", String(isOpen));
    });

    document.addEventListener("click", (event) => {
      if (!dropdown.contains(event.target as Node)) close();
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") close();
    });
  });
}

function initHeader(): void {
  const header = document.querySelector<HTMLElement>("[data-header]");
  const progress = document.querySelector<HTMLElement>(
    "[data-scroll-progress]",
  );
  const floatingSupport =
    document.querySelector<HTMLElement>(".floating-support");
  if (!header) return;

  const update = (): void => {
    header.classList.toggle("scrolled", window.scrollY > 56);
    const scrollable =
      document.documentElement.scrollHeight - window.innerHeight;
    const percentage = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 0;
    progress?.style.setProperty("--scroll-progress", `${percentage}%`);
    floatingSupport?.classList.toggle(
      "visible",
      window.scrollY > window.innerHeight * 0.72,
    );
  };
  update();
  window.addEventListener("scroll", update, { passive: true });
}

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function initPagePulse(): void {
  const pulse = document.querySelector<HTMLElement>("[data-page-pulse]");
  if (!pulse) return;

  let navigationTimer = 0;
  const resetNavigationState = (): void => {
    window.clearTimeout(navigationTimer);
    document.body.classList.remove("page-is-leaving");
    pulse.classList.remove("is-exiting");
  };

  // Les pages restaurées depuis le cache du navigateur conservent leur DOM.
  // On nettoie donc toujours l'état de sortie au retour arrière/avant.
  window.addEventListener("pageshow", resetNavigationState);
  window.addEventListener("pagehide", () => window.clearTimeout(navigationTimer));
  if (prefersReducedMotion()) return;

  document.addEventListener("click", (event) => {
    if (!(event instanceof MouseEvent) || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const anchor = (event.target as HTMLElement).closest<HTMLAnchorElement>("a[href]");
    if (!anchor || anchor.target || anchor.hasAttribute("download")) return;

    const href = anchor.getAttribute("href");
    if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return;

    const destination = new URL(anchor.href, window.location.href);
    if (destination.origin !== window.location.origin) return;
    if (
      destination.pathname === window.location.pathname &&
      destination.search === window.location.search &&
      destination.hash
    ) return;

    event.preventDefault();
    if (document.body.classList.contains("page-is-leaving")) return;

    document.body.classList.add("page-is-leaving");
    pulse.classList.add("is-exiting");
    navigationTimer = window.setTimeout(() => window.location.assign(destination.href), 460);
  });
}

function initHeroSequence(): void {
  const heroCopies = document.querySelectorAll<HTMLElement>(
    ".hero-content, .contact-hero-content, .about-hero-copy, .products-hero-copy, .support-hero-copy, .projects-hero-copy, .academy-hero-copy, .media-hero-inner, .articles-hero > .container, .article-detail-header > .container, .legal-hero > .container",
  );

  heroCopies.forEach((copy) => {
    copy.classList.add("hero-sequence");
    Array.from(copy.children).forEach((child, index) => {
      if (!(child instanceof HTMLElement)) return;
      child.classList.add("hero-step");
      child.style.setProperty("--hero-delay", `${620 + index * 85}ms`);
    });
  });
}

function initScrollMotion(): void {
  if (prefersReducedMotion()) return;
  const heroMedia = document.querySelector(
    ".hero-photo, .contact-hero-photo, .about-hero-photo, .products-hero-media, .support-hero-media, .projects-hero-media, .academy-hero-media",
  );
  if (!heroMedia) return;

  let target = Math.min(window.scrollY * 0.05, 32);
  let current = target;
  let frame = 0;

  const render = (): void => {
    current += (target - current) * 0.14;
    document.documentElement.style.setProperty(
      "--hero-parallax-y",
      `${current.toFixed(2)}px`,
    );

    if (Math.abs(target - current) > 0.08) {
      frame = requestAnimationFrame(render);
    } else {
      frame = 0;
    }
  };

  const update = (): void => {
    target = Math.min(window.scrollY * 0.05, 32);
    if (!frame) frame = requestAnimationFrame(render);
  };

  window.addEventListener("scroll", update, { passive: true });
}

function initRevealAnimations(): void {
  const elements = document.querySelectorAll<HTMLElement>("[data-reveal]");
  const staggerSelector =
    ".stat-card, .expertise-item, .partner-list strong, .offer-card, .project-card, .about-stat, .value-card, .mission-card, .team-card, .about-partner, .product-benefits article, .service-card, .recent-project-card, .project-domains-grid article, .academy-expertise-grid article, .academy-program-card, .academy-why-grid article, .related-card, .article-card";
  const headingSelector =
    ".section-heading, .about-section-heading, .projects-heading, .domains-heading, .contact-section-heading";
  const sectionStyles = ["reveal-rise", "reveal-from-start", "reveal-rise", "reveal-from-end"];
  const childStyles = ["reveal-child--start", "reveal-child--center", "reveal-child--end"];

  elements.forEach((element, sectionIndex) => {
    element.classList.add(sectionStyles[sectionIndex % sectionStyles.length]!);
    element
      .querySelectorAll<HTMLElement>(headingSelector)
      .forEach((heading) => heading.classList.add("reveal-heading"));

    element
      .querySelectorAll<HTMLElement>(staggerSelector)
      .forEach((child, index) => {
        child.classList.add(
          "reveal-child",
          childStyles[index % childStyles.length]!,
        );
        child.style.setProperty(
          "--reveal-delay",
          `${110 + Math.min(index, 7) * 72}ms`,
        );
      });
  });

  if (prefersReducedMotion() || !("IntersectionObserver" in window)) {
    elements.forEach((element) => element.classList.add("visible"));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("visible");
        window.setTimeout(
          () => entry.target.classList.add("reveal-complete"),
          1450,
        );
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.07, rootMargin: "0px 0px -10%" },
  );

  elements.forEach((element) => observer.observe(element));
}

function initProductScrollReveal(): void {
  const grid = document.querySelector<HTMLElement>("[data-products-grid]");
  if (!grid) return;

  const cards = Array.from(
    grid.querySelectorAll<HTMLElement>("[data-product-card]"),
  );
  cards.forEach((card) => card.classList.add("catalog-scroll-reveal"));

  if (prefersReducedMotion() || !("IntersectionObserver" in window)) {
    cards.forEach((card) =>
      card.classList.add("catalog-reveal-visible", "catalog-reveal-complete"),
    );
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => {
          const topDifference = a.boundingClientRect.top - b.boundingClientRect.top;
          return Math.abs(topDifference) > 8
            ? topDifference
            : a.boundingClientRect.left - b.boundingClientRect.left;
        })
        .forEach((entry, batchIndex) => {
          const card = entry.target as HTMLElement;
          const delay = Math.min(batchIndex, 7) * 85;
          card.style.setProperty("--catalog-scroll-delay", `${delay}ms`);
          card.classList.add("catalog-reveal-visible");
          window.setTimeout(
            () => card.classList.add("catalog-reveal-complete"),
            delay + 780,
          );
          observer.unobserve(card);
        });
    },
    { threshold: 0.12, rootMargin: "0px 0px -4%" },
  );

  cards.forEach((card) => observer.observe(card));
}

function initMediaLibraryMotion(): void {
  const library = document.querySelector<HTMLElement>("[data-media-library]");
  if (!library) return;

  const cards = Array.from(
    library.querySelectorAll<HTMLElement>("[data-media-card]"),
  );
  const finePointer = window.matchMedia("(pointer: fine)").matches;

  cards.forEach((card, index) => {
    card.classList.add("media-motion-ready");
    card.style.setProperty("--media-delay", `${Math.min(index, 5) * 125}ms`);
    card.style.setProperty("--media-entry-x", `${index % 2 === 0 ? -1.4 : 1.4}rem`);

    if (!finePointer || prefersReducedMotion()) return;
    card.addEventListener("pointermove", (event) => {
      const bounds = card.getBoundingClientRect();
      const x = (event.clientX - bounds.left) / bounds.width;
      const y = (event.clientY - bounds.top) / bounds.height;
      card.style.setProperty("--media-tilt-x", `${((0.5 - y) * 3.4).toFixed(2)}deg`);
      card.style.setProperty("--media-tilt-y", `${((x - 0.5) * 4.2).toFixed(2)}deg`);
      card.style.setProperty("--media-light-x", `${(x * 100).toFixed(1)}%`);
      card.style.setProperty("--media-light-y", `${(y * 100).toFixed(1)}%`);
    });
    card.addEventListener("pointerleave", () => {
      card.style.setProperty("--media-tilt-x", "0deg");
      card.style.setProperty("--media-tilt-y", "0deg");
      card.style.setProperty("--media-light-x", "50%");
      card.style.setProperty("--media-light-y", "35%");
    });
  });

  if (prefersReducedMotion() || !("IntersectionObserver" in window)) {
    cards.forEach((card) => card.classList.add("is-media-visible"));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.left - b.boundingClientRect.left)
        .forEach((entry) => {
          const card = entry.target as HTMLElement;
          card.classList.add("is-media-visible");
          observer.unobserve(card);
        });
    },
    { threshold: 0.18, rootMargin: "0px 0px -7%" },
  );

  cards.forEach((card) => observer.observe(card));
}

function initActiveNavigation(): void {
  const links = Array.from(
    document.querySelectorAll<HTMLAnchorElement>(
      '.main-nav > a[href^="#"]:not(.button)',
    ),
  );
  const sections = links
    .map((link) => {
      const id = link.getAttribute("href")?.slice(1);
      const section = id ? document.getElementById(id) : null;
      return section ? { link, section } : null;
    })
    .filter((item): item is { link: HTMLAnchorElement; section: HTMLElement } =>
      Boolean(item),
    );

  if (!sections.length || !("IntersectionObserver" in window)) return;

  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!visible) return;

      links.forEach((link) => link.classList.remove("active"));
      sections
        .find(({ section }) => section === visible.target)
        ?.link.classList.add("active");
    },
    { rootMargin: "-22% 0px -62%", threshold: [0, 0.1, 0.25] },
  );

  sections.forEach(({ section }) => observer.observe(section));
}

function initCounters(): void {
  const counters = document.querySelectorAll<HTMLElement>("[data-count]");

  counters.forEach((counter) => {
    const target = Number(counter.dataset.count);
    const suffix = counter.dataset.suffix ?? "";
    let started = false;

    const observer = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting || started) return;
      started = true;
      const start = performance.now();

      const animate = (time: number): void => {
        const progress = Math.min((time - start) / 900, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        counter.textContent = `${Math.round(target * eased)}${suffix}`;
        if (progress < 1) requestAnimationFrame(animate);
      };

      requestAnimationFrame(animate);
      observer.disconnect();
    });

    observer.observe(counter);
  });
}

function initHeroControls(): void {
  const hero = document.querySelector<HTMLElement>("[data-hero-slider]");
  const photo = hero?.querySelector<HTMLElement>(".hero-photo");
  const title = hero?.querySelector<HTMLElement>("[data-hero-title]");
  const description = hero?.querySelector<HTMLElement>("[data-hero-description]");
  const current = hero?.querySelector<HTMLElement>("[data-hero-current]");
  const previous = hero?.querySelector<HTMLButtonElement>("[data-hero-prev]");
  const next = hero?.querySelector<HTMLButtonElement>("[data-hero-next]");
  if (!hero || !photo || !title || !description || !current || !previous || !next) return;

  const slides = [
    {
      title: "Innover<br />à chaque<br /><span>fourniture.</span>",
      description: "Des équipements et solutions professionnelles choisis pour renforcer durablement vos opérations.",
      image: "/assets/images/globaltech-technicien-radio.jpg",
      position: "68% 30%",
    },
    {
      title: "Équiper<br />vos équipes.<br /><span>Renforcer le terrain.</span>",
      description: "Des radios et accessoires certifiés, configurés pour les environnements les plus exigeants.",
      image: "/assets/images/globaltech-ingenieur-terrain.jpg",
      position: "62% 35%",
    },
    {
      title: "Connecter<br />vos sites.<br /><span>Sécuriser les échanges.</span>",
      description: "Une expertise complète, de l’étude au déploiement, avec un support technique de proximité.",
      image: "/assets/images/globaltech-antenne-satellite.jpg",
      position: "42% 35%",
    },
  ];

  let slide = 0;
  let rotationTimer = 0;
  let transitionTimer = 0;

  const displaySlide = (nextIndex: number): void => {
    if (nextIndex === slide) return;
    slide = (nextIndex + slides.length) % slides.length;
    hero.classList.add("is-slide-changing");
    window.clearTimeout(transitionTimer);
    transitionTimer = window.setTimeout(() => {
      const content = slides[slide]!;
      title.innerHTML = content.title;
      description.textContent = content.description;
      current.textContent = `0${slide + 1}`;
      photo.style.setProperty("--hero-image", `url('${content.image}')`);
      photo.style.backgroundPosition = content.position;
      requestAnimationFrame(() => hero.classList.remove("is-slide-changing"));
    }, 280);
  };

  const startRotation = (): void => {
    window.clearInterval(rotationTimer);
    if (!prefersReducedMotion()) {
      rotationTimer = window.setInterval(() => displaySlide(slide + 1), 5000);
    }
  };

  previous.addEventListener("click", () => { displaySlide(slide - 1); startRotation(); });
  next.addEventListener("click", () => { displaySlide(slide + 1); startRotation(); });
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) window.clearInterval(rotationTimer);
    else startRotation();
  });
  startRotation();
}

function initStoryDialog(): void {
  const dialog = document.querySelector<HTMLDialogElement>(
    "[data-story-dialog]",
  );
  const openButtons = Array.from(
    document.querySelectorAll<HTMLButtonElement>("[data-story-open]"),
  );
  const closeButton =
    document.querySelector<HTMLButtonElement>("[data-story-close]");
  const video = dialog?.querySelector<HTMLIFrameElement>("[data-story-video]");
  const title = dialog?.querySelector<HTMLElement>("[data-story-video-title]");
  if (!dialog || !openButtons.length || !closeButton || !video || !title) return;

  let lastTrigger: HTMLButtonElement | null = null;
  const closeDialog = (): void => {
    if (dialog.open) dialog.close();
  };

  openButtons.forEach((openButton) => {
    openButton.addEventListener("click", () => {
      const source = openButton.dataset.videoSrc;
      if (!source) return;
      lastTrigger = openButton;
      title.textContent = openButton.dataset.videoTitle ?? "Vidéo de présentation";
      video.title = title.textContent;
      video.src = source;
      dialog.showModal();
    });
  });
  closeButton.addEventListener("click", closeDialog);
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) closeDialog();
  });
  dialog.addEventListener("close", () => {
    video.src = "about:blank";
    lastTrigger?.focus();
  });
}

function initBackToTop(): void {
  const link = document.querySelector<HTMLAnchorElement>("[data-back-to-top]");
  if (!link) return;

  link.addEventListener("click", (event) => {
    event.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
  });
}

const COOKIE_CONSENT_KEY = "gt-cookie-consent";
const COOKIE_CONSENT_VERSION = 2;
// Le choix est redemandé après 6 mois (recommandation CNIL).
const COOKIE_CONSENT_MAX_AGE = 1000 * 60 * 60 * 24 * 182;

type ConsentCategory = "media";
type CookieConsent = { version: number; date: string; media: boolean };

function readCookieConsent(): CookieConsent | null {
  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    const consent = JSON.parse(raw) as Partial<CookieConsent>;
    const age = Date.now() - new Date(consent.date ?? 0).getTime();
    if (consent.version !== COOKIE_CONSENT_VERSION || !(age >= 0 && age < COOKIE_CONSENT_MAX_AGE)) return null;
    return { version: consent.version, date: consent.date as string, media: Boolean(consent.media) };
  } catch {
    // Ancien format ("accepted"/"declined") ou stockage indisponible : on redemande le choix.
    return null;
  }
}

function writeCookieConsent(choices: Pick<CookieConsent, ConsentCategory>): void {
  const consent: CookieConsent = { version: COOKIE_CONSENT_VERSION, date: new Date().toISOString(), ...choices };
  try {
    window.localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
  } catch {
    // Stockage indisponible (navigation privée, etc.) : le choix vaut pour la page en cours.
  }
  document.dispatchEvent(new CustomEvent("gt:consent-change", { detail: consent }));
}

function hasConsent(category: ConsentCategory): boolean {
  return readCookieConsent()?.[category] ?? false;
}

function initCookieConsent(): void {
  const banner = document.querySelector<HTMLElement>("[data-cookie-consent]");
  if (!banner) return;

  const preferences = banner.querySelector<HTMLElement>("[data-cookie-preferences]");
  const customize = banner.querySelector<HTMLButtonElement>("[data-cookie-customize]");
  const save = banner.querySelector<HTMLButtonElement>("[data-cookie-save]");
  const mediaToggle = banner.querySelector<HTMLInputElement>('[data-cookie-category="media"]');

  const setPreferencesOpen = (open: boolean): void => {
    if (preferences) preferences.hidden = !open;
    if (customize) {
      customize.hidden = open;
      customize.setAttribute("aria-expanded", String(open));
    }
    if (save) save.hidden = !open;
    banner.classList.toggle("is-expanded", open);
  };

  const showBanner = (withPreferences = false): void => {
    if (mediaToggle) mediaToggle.checked = hasConsent("media");
    setPreferencesOpen(withPreferences);
    banner.hidden = false;
    requestAnimationFrame(() => banner.classList.add("is-visible"));
    if (withPreferences) window.setTimeout(() => mediaToggle?.focus(), 80);
  };

  const hideBanner = (): void => {
    banner.classList.remove("is-visible");
    window.setTimeout(() => {
      banner.hidden = true;
    }, 420);
  };

  const decide = (media: boolean): void => {
    writeCookieConsent({ media });
    hideBanner();
  };

  if (!readCookieConsent()) showBanner();

  banner.querySelector("[data-cookie-accept]")?.addEventListener("click", () => decide(true));
  banner.querySelector("[data-cookie-decline]")?.addEventListener("click", () => decide(false));
  save?.addEventListener("click", () => decide(Boolean(mediaToggle?.checked)));
  customize?.addEventListener("click", () => {
    setPreferencesOpen(true);
    mediaToggle?.focus();
  });

  // Échap ferme le bandeau seulement si un choix existe déjà (réouverture via « Gérer les cookies »).
  banner.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && readCookieConsent()) hideBanner();
  });

  document.addEventListener("click", (event) => {
    const trigger = (event.target as Element | null)?.closest("[data-cookie-settings]");
    if (!trigger) return;
    event.preventDefault();
    showBanner(true);
  });
}

// Contenus tiers (Google Maps…) chargés uniquement après consentement.
function initConsentEmbeds(): void {
  const embeds = Array.from(document.querySelectorAll<HTMLElement>("[data-consent-embed]"));
  if (!embeds.length) return;

  const load = (embed: HTMLElement): void => {
    const frame = embed.querySelector<HTMLIFrameElement>("iframe[data-src]");
    if (frame && !frame.src) frame.src = frame.dataset.src ?? "";
    if (frame) frame.hidden = false;
    embed.querySelector<HTMLElement>("[data-consent-placeholder]")?.setAttribute("hidden", "");
    embed.classList.add("is-loaded");
  };

  const refresh = (): void => {
    embeds.forEach((embed) => {
      if (hasConsent((embed.dataset.consentEmbed ?? "media") as ConsentCategory)) load(embed);
    });
  };

  embeds.forEach((embed) => {
    embed.querySelector("[data-consent-enable]")?.addEventListener("click", () => {
      writeCookieConsent({ media: true });
      const banner = document.querySelector<HTMLElement>("[data-cookie-consent]");
      if (banner && !banner.hidden) {
        banner.classList.remove("is-visible");
        window.setTimeout(() => {
          banner.hidden = true;
        }, 420);
      }
    });
  });

  document.addEventListener("gt:consent-change", refresh);
  refresh();
}

function initPartnersCarousel(): void {
  const partners = document.querySelector<HTMLElement>("[data-partners]");
  const track = partners?.querySelector<HTMLElement>("[data-partner-track]");
  const next = partners?.querySelector<HTMLButtonElement>("[data-partner-next]");
  const prev = partners?.querySelector<HTMLButtonElement>("[data-partner-prev]");
  if (!partners || !track || !next || !prev) return;

  const update = (): void => {
    const hasOverflow = track.scrollWidth - track.clientWidth > 3;
    const atStart = track.scrollLeft <= 3;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 3;
    next.hidden = !hasOverflow || atEnd;
    prev.hidden = !hasOverflow || atStart;
  };

  next.addEventListener("click", () => {
    track.scrollTo({
      left: Math.min(track.scrollLeft + track.clientWidth * 0.8, track.scrollWidth),
      behavior: "smooth",
    });
  });
  prev.addEventListener("click", () => {
    track.scrollTo({
      left: Math.max(track.scrollLeft - track.clientWidth * 0.8, 0),
      behavior: "smooth",
    });
  });
  track.addEventListener("scroll", update, { passive: true });
  window.addEventListener("resize", update, { passive: true });
  requestAnimationFrame(update);
}

function initProjectsGallery(): void {
  const gallery = document.querySelector<HTMLElement>("[data-projects-gallery]");
  if (!gallery) return;

  const buttons = Array.from(
    gallery.querySelectorAll<HTMLButtonElement>("[data-project-filter]"),
  );
  const cards = Array.from(
    gallery.querySelectorAll<HTMLElement>("[data-project-card]"),
  );
  const more = gallery.querySelector<HTMLButtonElement>("[data-project-more]");
  const requestedDomain = new URLSearchParams(window.location.search).get("domain") ?? "all";
  const allowedDomains = new Set(buttons.map((button) => button.dataset.projectFilter ?? "all"));
  let activeDomain = allowedDomains.has(requestedDomain) ? requestedDomain : "all";
  let expanded = activeDomain !== "all";

  const apply = (): void => {
    let visibleIndex = 0;
    cards.forEach((card) => {
      const matches = activeDomain === "all" || card.dataset.projectDomain === activeDomain;
      const withinLimit = expanded || activeDomain !== "all" || visibleIndex < 4;
      const visible = matches && withinLimit;
      if (matches) visibleIndex += 1;

      card.classList.toggle("is-project-filtered", !matches);
      card.classList.toggle("is-project-hidden", matches && !withinLimit);
      card.classList.remove("is-project-entering");
      if (visible) requestAnimationFrame(() => card.classList.add("is-project-entering"));
    });

    buttons.forEach((button) => {
      const active = button.dataset.projectFilter === activeDomain;
      button.classList.toggle("active", active);
      button.setAttribute("aria-pressed", String(active));
    });
    if (more) more.hidden = activeDomain !== "all" || expanded || cards.length <= 4;
  };

  buttons.forEach((button) => {
    button.addEventListener("click", () => {
      activeDomain = button.dataset.projectFilter ?? "all";
      expanded = activeDomain !== "all";
      const url = new URL(window.location.href);
      if (activeDomain === "all") url.searchParams.delete("domain");
      else url.searchParams.set("domain", activeDomain);
      window.history.replaceState({}, "", `${url.pathname}${url.search}#portfolio`);
      apply();
    });
  });

  more?.addEventListener("click", () => {
    expanded = true;
    apply();
  });
  apply();
}

// ═══════════════════════════════════════
//  VALIDATION DES FORMULAIRES (form[data-validate])
// ═══════════════════════════════════════
// Messages en français, affichés sous chaque champ au fil de la saisie et à l'envoi.
// Les mêmes règles sont revérifiées côté serveur (src/validation/forms.ts).

type ValidatedField = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function formatDate(value: string): string {
  const [year, month, day] = value.split("-");
  return day && month && year ? `${day}/${month}/${year}` : value;
}

function fieldValidationMessage(field: ValidatedField): string {
  const { dataset } = field;

  if (field instanceof HTMLInputElement && field.type === "checkbox") {
    return field.required && !field.checked ? dataset.msgRequired ?? "Cette case doit être cochée." : "";
  }

  if (field instanceof HTMLInputElement && field.type === "file") {
    const files = Array.from(field.files ?? []);
    if (field.required && !files.length) return dataset.msgRequired ?? "Ajoutez un fichier.";
    const maxFiles = Number(dataset.maxFiles);
    if (maxFiles && files.length > maxFiles) return `${maxFiles} fichiers au maximum (${files.length} sélectionnés).`;
    const accepted = field.accept.split(",").map((type) => type.trim()).filter(Boolean);
    const wrongType = files.find((file) => accepted.length && !accepted.includes(file.type));
    if (wrongType) return `« ${wrongType.name} » n’est pas une image acceptée (JPG, PNG, WebP, AVIF ou GIF).`;
    const maxSizeMb = Number(dataset.maxSizeMb);
    const tooBig = files.find((file) => maxSizeMb && file.size > maxSizeMb * 1024 * 1024);
    if (tooBig) return `« ${tooBig.name} » dépasse ${maxSizeMb} Mo (${(tooBig.size / 1024 / 1024).toFixed(1)} Mo).`;
    return "";
  }

  const value = field.value.trim();
  if (!value) {
    if (!field.required) return "";
    return dataset.msgRequired ?? (field instanceof HTMLSelectElement ? "Sélectionnez une option." : "Ce champ est obligatoire.");
  }
  if (field instanceof HTMLSelectElement) return "";

  const length = [...value].length;
  if (field.type === "email" && !EMAIL_PATTERN.test(value)) return "Saisissez une adresse email valide (ex. nom@domaine.com).";
  if (field.minLength > 0 && length < field.minLength) return `Au moins ${field.minLength} caractères (actuellement ${length}).`;
  if (field.maxLength > 0 && length > field.maxLength) return `${field.maxLength} caractères maximum (actuellement ${length}).`;

  if (field instanceof HTMLInputElement) {
    if (field.pattern && !new RegExp(`^(?:${field.pattern})$`, "u").test(value)) {
      return dataset.msgPattern ?? "Le format de ce champ n’est pas valide.";
    }
    if (field.type === "number") {
      const number = Number(value);
      if (!Number.isFinite(number) || field.validity.badInput) return "Saisissez un nombre valide.";
      if (field.step === "1" && !Number.isInteger(number)) return "Saisissez un nombre entier.";
      if (field.min !== "" && number < Number(field.min)) return `La valeur minimale est ${field.min}.`;
      if (field.max !== "" && number > Number(field.max)) return `La valeur maximale est ${field.max}.`;
    }
    if (field.type === "date") {
      if (field.validity.badInput) return "Saisissez une date valide.";
      if (field.min && value < field.min) return dataset.msgMin ?? `La date doit être au plus tôt le ${formatDate(field.min)}.`;
      if (field.max && value > field.max) return `La date doit être au plus tard le ${formatDate(field.max)}.`;
    }
  }
  return "";
}

function setFieldError(field: ValidatedField, message: string): void {
  const errorId = `${field.id || field.name}-error`;
  let error = document.getElementById(errorId);

  if (message && !error) {
    error = document.createElement("p");
    error.className = "field-error";
    error.id = errorId;
    // Sous la liste déroulante, la case à cocher ou l'aide éventuelle du champ
    let anchor: Element = field.closest(".select-wrapper, .form-checkbox") ?? field;
    const counter = anchor.nextElementSibling;
    if (counter?.classList.contains("field-counter")) anchor = counter;
    if (anchor.nextElementSibling?.classList.contains("field-hint")) anchor = anchor.nextElementSibling;
    anchor.insertAdjacentElement("afterend", error);
  }
  if (error) {
    error.textContent = message;
    error.hidden = !message;
  }

  const describedBy = new Set((field.getAttribute("aria-describedby") ?? "").split(" ").filter(Boolean));
  if (message) {
    field.setAttribute("aria-invalid", "true");
    describedBy.add(errorId);
  } else {
    field.removeAttribute("aria-invalid");
    describedBy.delete(errorId);
  }
  if (describedBy.size) field.setAttribute("aria-describedby", [...describedBy].join(" "));
  else field.removeAttribute("aria-describedby");
}

function initCharacterCounters(scope: ParentNode): void {
  scope.querySelectorAll<HTMLTextAreaElement>("textarea[data-counter][maxlength]").forEach((field) => {
    const counter = document.createElement("span");
    counter.className = "field-counter";
    counter.setAttribute("aria-hidden", "true");
    field.insertAdjacentElement("afterend", counter);
    const update = (): void => {
      const length = [...field.value].length;
      counter.textContent = `${length} / ${field.maxLength}`;
      counter.classList.toggle("is-near-limit", length >= field.maxLength * 0.9);
    };
    field.addEventListener("input", update);
    update();
  });
}

function initFormValidation(): void {
  document.querySelectorAll<HTMLFormElement>("form[data-validate]").forEach((form) => {
    // La validation native reste active sans JavaScript ; ici on la remplace par nos messages.
    form.noValidate = true;
    initCharacterCounters(form);

    const fields = (): ValidatedField[] =>
      Array.from(form.elements).filter(
        (element): element is ValidatedField =>
          (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement)
          && Boolean(element.name)
          && !element.disabled
          && !["hidden", "submit", "button"].includes(element.type)
          && !element.closest(".form-honeypot"),
      );

    const touched = new WeakSet<Element>();
    const validate = (field: ValidatedField): boolean => {
      const message = fieldValidationMessage(field);
      setFieldError(field, message);
      return !message;
    };

    form.addEventListener("focusout", (event) => {
      const field = event.target as ValidatedField;
      if (!fields().includes(field) || (!field.value && !touched.has(field))) return;
      touched.add(field);
      validate(field);
    });

    const revalidate = (event: Event): void => {
      const field = event.target as ValidatedField;
      if (!fields().includes(field)) return;
      if (event.type === "change") touched.add(field);
      // Une erreur affichée disparaît dès que la saisie devient correcte
      if (touched.has(field) || field.getAttribute("aria-invalid") === "true") validate(field);
    };
    form.addEventListener("input", revalidate);
    form.addEventListener("change", revalidate);

    form.addEventListener("submit", (event) => {
      const invalid = fields().filter((field) => !validate(field));
      let summary = form.previousElementSibling?.matches("[data-form-alert]")
        ? (form.previousElementSibling as HTMLElement)
        : null;

      if (!invalid.length) {
        summary?.remove();
        const submit = form.querySelector<HTMLButtonElement>('[type="submit"]');
        // Évite les doubles envois pendant le chargement de la page suivante
        window.setTimeout(() => {
          if (submit) submit.disabled = true;
          form.setAttribute("aria-busy", "true");
        }, 0);
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      if (!summary) {
        summary = document.createElement("div");
        summary.className = "form-alert form-alert--error";
        summary.setAttribute("role", "alert");
        summary.dataset.formAlert = "";
        form.insertAdjacentElement("beforebegin", summary);
      }
      summary.textContent = invalid.length === 1
        ? "Le formulaire contient une erreur. Corrigez le champ signalé."
        : `Le formulaire contient ${invalid.length} erreurs. Corrigez les champs signalés.`;
      invalid[0].focus();
    });
  });

  // Page renvoyée par le serveur avec des erreurs : on amène l'utilisateur au formulaire
  const serverAlert = document.querySelector<HTMLElement>("[data-form-alert]");
  if (serverAlert) {
    serverAlert.scrollIntoView({ block: "center" });
    serverAlert.focus({ preventScroll: true });
  }
}

function initContactContext(): void {
  const form = document.querySelector<HTMLFormElement>("#contactForm");
  if (!form) return;
  const subject = form.querySelector<HTMLSelectElement>("#contactSubject");
  const message = form.querySelector<HTMLTextAreaElement>("#contactMessage");
  const params = new URLSearchParams(window.location.search);
  const requestedSubject = params.get("sujet");
  const product = params.get("produit");
  const program = params.get("programme");
  const service = params.get("service");

  const context = product
    ? { subject: "produit", message: `Je souhaite obtenir plus d'informations sur le produit n°${product}.` }
    : program
      ? { subject: "formation", message: `Je souhaite obtenir plus d'informations sur le programme de formation n°${program}.` }
      : service
        ? { subject: "support", message: `Je souhaite être accompagné concernant le service « ${service} ».` }
        : requestedSubject
          ? { subject: requestedSubject, message: "Je souhaite échanger avec votre équipe à ce sujet." }
          : null;

  if (!context) return;
  if (subject && Array.from(subject.options).some((option) => option.value === context.subject)) {
    subject.value = context.subject;
  }
  if (message && !message.value) {
    message.value = context.message;
    message.dispatchEvent(new Event("input", { bubbles: true })); // met à jour le compteur de caractères
  }
}

// Loader dédié à l'enregistrement du message de contact (basé sur .page-pulse,
// déclenché manuellement au submit plutôt qu'automatiquement à chaque chargement de page).
function initMessagePulse(): void {
  const form = document.querySelector<HTMLFormElement>("#contactForm");
  const pulse = document.querySelector<HTMLElement>("[data-message-pulse]");
  if (!form || !pulse || prefersReducedMotion()) return;

  form.addEventListener("submit", (event) => {
    // Pas d'animation de départ si la validation a bloqué l'envoi
    if (event.defaultPrevented) return;
    document.body.classList.add("page-is-leaving");
    pulse.classList.add("is-exiting");
  });
}

function initProductGallery(): void {
  const mainVisual = document.querySelector<HTMLElement>("[data-product-main-visual]");
  const thumbs = Array.from(
    document.querySelectorAll<HTMLButtonElement>("[data-product-thumb]"),
  );
  if (!mainVisual || !thumbs.length) return;

  thumbs.forEach((thumb) => {
    thumb.addEventListener("click", () => {
      const mainStyle = mainVisual.getAttribute("style") ?? "";
      const mainLabel = mainVisual.getAttribute("aria-label") ?? "";
      const thumbStyle = thumb.getAttribute("style") ?? "";
      const thumbLabel = thumb.getAttribute("aria-label") ?? "";

      mainVisual.setAttribute("style", thumbStyle);
      mainVisual.setAttribute("aria-label", thumbLabel);
      thumb.setAttribute("style", mainStyle);
      thumb.setAttribute("aria-label", mainLabel);

      thumbs.forEach((item) => item.classList.remove("active"));
    });
  });
}

function initProductCatalog(): void {
  const catalog = document.querySelector<HTMLElement>("[data-product-catalog]");
  const grid = catalog?.querySelector<HTMLElement>("[data-products-grid]");
  if (!catalog || !grid) return;

  const cards = Array.from(
    grid.querySelectorAll<HTMLElement>("[data-product-card]"),
  );
  const filters = Array.from(
    catalog.querySelectorAll<HTMLInputElement>("[data-product-filter]"),
  );
  const count = catalog.querySelector<HTMLElement>("[data-product-count]");
  const emptyState = catalog.querySelector<HTMLElement>("[data-catalog-empty]");
  const sort = catalog.querySelector<HTMLSelectElement>("[data-product-sort]");
  const filtersPanel = catalog.querySelector<HTMLElement>("[data-filters-panel]");
  const filterToggle = catalog.querySelector<HTMLButtonElement>(
    "[data-filter-toggle]",
  );
  const paginationNav = catalog.querySelector<HTMLElement>("[data-catalog-pagination]");
  const PAGE_SIZE = 12;
  let currentPage = 1;

  const query = new URLSearchParams(window.location.search);
  const filterAliases: Record<string, string[]> = {
    analog: ["analog", "Radios analogiques"],
    "digital-dmr": ["digital-dmr", "Radios numériques DMR"],
    "digital-tetra": ["digital-tetra", "Radios numériques TETRA"],
    infrastructure: ["infrastructure", "Infrastructure & réseaux"],
    security: ["security", "Solutions de sécurité"],
    mobile: ["mobile", "Mobile / Base"],
    portable: ["portable", "Portable"],
    repeaters: ["repeaters", "Répéteurs"],
    "control-room": ["control-room", "Solutions salle de contrôle"],
    motorola: ["motorola", "Motorola Solutions"],
    hytera: ["hytera", "Hytera"],
    icom: ["icom", "ICOM"],
    sepura: ["sepura", "Sepura"],
  };
  (["solution", "type", "brand"] as const).forEach((group) => {
    query.getAll(group).flatMap((value) => value.split(",")).forEach((value) => {
      const acceptedValues = filterAliases[value] ?? [value];
      const matchingFilter = filters.find(
        (filter) => filter.dataset.filterGroup === group && acceptedValues.includes(filter.value),
      );
      if (matchingFilter) matchingFilter.checked = true;
    });
  });

  const getActiveFilters = (): Map<string, Set<string>> => {
    const active = new Map<string, Set<string>>();
    filters
      .filter((filter) => filter.checked)
      .forEach((filter) => {
        const group = filter.dataset.filterGroup ?? "";
        const values = active.get(group) ?? new Set<string>();
        values.add(filter.value);
        active.set(group, values);
      });
    return active;
  };

  const getMatchingCards = (): HTMLElement[] =>
    Array.from(grid.querySelectorAll<HTMLElement>("[data-product-card]:not(.is-filtered-out)"));

  const renderPagination = (totalPages: number): void => {
    if (!paginationNav) return;
    paginationNav.innerHTML = "";
    // Les flèches restent affichées même s'il n'y a qu'une page (elles sont alors désactivées)
    paginationNav.hidden = false;

    const goToPage = (page: number): void => {
      currentPage = page;
      applyPagination();
      catalog.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
    };

    const arrow = (label: string, icon: string, target: number): HTMLElement => {
      const enabled = target >= 1 && target <= totalPages && target !== currentPage;
      const element = document.createElement(enabled ? "a" : "span");
      element.className = "pagination-arrow";
      element.setAttribute("aria-label", label);
      element.innerHTML = `<i data-icon="${icon}"></i>`;
      if (enabled) {
        (element as HTMLAnchorElement).href = "#products-top";
        element.addEventListener("click", (event) => {
          event.preventDefault();
          goToPage(target);
        });
      } else {
        element.setAttribute("aria-disabled", "true");
        element.classList.add("is-disabled");
      }
      return element;
    };

    paginationNav.append(arrow("Page précédente", "arrow-left", currentPage - 1));

    for (let page = 1; page <= totalPages; page += 1) {
      const link = document.createElement("a");
      link.href = "#products-top";
      link.textContent = String(page);
      link.classList.toggle("active", page === currentPage);
      link.setAttribute("aria-current", page === currentPage ? "page" : "false");
      link.addEventListener("click", (event) => {
        event.preventDefault();
        goToPage(page);
      });
      paginationNav.append(link);
    }

    paginationNav.append(arrow("Page suivante", "arrow-right", currentPage + 1));

    renderIcons();
  };

  const applyPagination = (): void => {
    const matching = getMatchingCards();
    const totalPages = Math.max(1, Math.ceil(matching.length / PAGE_SIZE));
    currentPage = Math.min(currentPage, totalPages);
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;

    matching.forEach((card, index) => {
      const isOnPage = index >= start && index < end;
      card.classList.toggle("is-page-hidden", !isOnPage);
      card.classList.remove("is-filtering-in");
      if (isOnPage) {
        card.style.setProperty("--filter-delay", `${(index - start) * 35}ms`);
        requestAnimationFrame(() => card.classList.add("is-filtering-in"));
      }
    });

    renderPagination(totalPages);
  };

  const applyFilters = (): void => {
    const active = getActiveFilters();
    let visibleCount = 0;

    cards.forEach((card) => {
      const isVisible = Array.from(active.entries()).every(
        ([group, values]) => values.has(card.dataset[group] ?? ""),
      );
      card.classList.toggle("is-filtered-out", !isVisible);
      if (isVisible) visibleCount += 1;
    });

    if (count) count.textContent = String(visibleCount);
    if (emptyState) emptyState.hidden = visibleCount !== 0;
    grid.hidden = visibleCount === 0;

    currentPage = 1;
    applyPagination();
  };

  const applySort = (): void => {
    const mode = sort?.value ?? "popularity";
    const sorted = [...cards].sort((a, b) => {
      if (mode === "name-asc") {
        return (a.dataset.productName ?? "").localeCompare(
          b.dataset.productName ?? "",
          "fr",
        );
      }
      if (mode === "name-desc") {
        return (b.dataset.productName ?? "").localeCompare(
          a.dataset.productName ?? "",
          "fr",
        );
      }
      return Number(b.dataset.popularity) - Number(a.dataset.popularity);
    });
    sorted.forEach((card) => grid.append(card));
  };

  filters.forEach((filter) => filter.addEventListener("change", applyFilters));
  sort?.addEventListener("change", () => {
    applySort();
    applyFilters();
  });

  catalog.querySelectorAll<HTMLButtonElement>("[data-filter-reset]").forEach(
    (button) => {
      button.addEventListener("click", () => {
        filters.forEach((filter) => {
          filter.checked = false;
        });
        applyFilters();
      });
    },
  );

  const setFiltersCollapsed = (collapsed: boolean): void => {
    filtersPanel?.classList.toggle("is-collapsed", collapsed);
    filterToggle?.setAttribute("aria-expanded", String(!collapsed));
    const label = filterToggle?.querySelector("span");
    if (label) label.textContent = collapsed ? "Afficher les filtres" : "Masquer les filtres";
  };

  // Replié par défaut (accès via la navigation) ; déplié si un lien a pré-sélectionné un filtre.
  setFiltersCollapsed(!filters.some((filter) => filter.checked));

  filterToggle?.addEventListener("click", () => {
    setFiltersCollapsed(!filtersPanel?.classList.contains("is-collapsed"));
  });

  catalog.querySelectorAll<HTMLButtonElement>("[data-product-view]").forEach(
    (button) => {
      button.addEventListener("click", () => {
        const view = button.dataset.productView;
        grid.classList.toggle("list", view === "list");
        catalog
          .querySelectorAll<HTMLElement>("[data-product-view]")
          .forEach((item) => item.classList.toggle("active", item === button));
      });
    },
  );

  applySort();
  applyFilters();
}

function initAcademyFilters(): void {
  const catalog = document.querySelector<HTMLElement>("[data-academy-catalog]");
  if (!catalog) return;

  const buttons = Array.from(
    catalog.querySelectorAll<HTMLButtonElement>("[data-academy-filter]"),
  );
  const cards = Array.from(
    catalog.querySelectorAll<HTMLElement>("[data-academy-card]"),
  );

  buttons.forEach((button) => {
    button.addEventListener("click", () => {
      const filter = button.dataset.academyFilter ?? "all";
      buttons.forEach((item) => item.classList.toggle("active", item === button));
      cards.forEach((card) => {
        const isVisible = filter === "all" || card.dataset.academyCard === filter;
        card.classList.toggle("is-hidden", !isVisible);
        if (isVisible) {
          card.classList.remove("is-filtering-in");
          requestAnimationFrame(() => card.classList.add("is-filtering-in"));
        }
      });
    });
  });
}

// Réseau de particules animées façon particles.js, sur toutes les pages publiques.
// Calque fixe transparent : les couleurs s'adaptent au fond de la section survolée
// (sections marquées data-particles-theme="dark" = fond sombre).
function initPageParticles(): void {
  const canvas = document.querySelector<HTMLCanvasElement>("[data-page-particles]");
  const context = canvas?.getContext("2d");
  if (!canvas || !context) return;

  type Particle = { x: number; y: number; vx: number; vy: number; radius: number; tone: number };

  // Même indice de teinte pour les deux thèmes : [fond sombre, fond clair]
  const palettes = {
    dark: ["255, 255, 255", "255, 255, 255", "255, 255, 255", "0, 189, 247", "0, 189, 247", "255, 147, 0"],
    light: ["3, 76, 176", "3, 76, 176", "3, 76, 176", "0, 75, 239", "10, 126, 240", "255, 147, 0"],
  };
  const linkDistance = 110;
  const pointerDistance = 150;
  const reducedMotion = prefersReducedMotion();
  const pointer = { x: 0, y: 0, active: false };
  let particles: Particle[] = [];
  let darkZones: Array<[number, number]> = [];
  let width = 0;
  let height = 0;
  let frame = 0;
  let lastTime = 0;
  let lastScroll = window.scrollY;

  const isDark = (y: number): boolean => darkZones.some(([top, bottom]) => y >= top && y <= bottom);

  const measureZones = (): void => {
    darkZones = Array.from(document.querySelectorAll<HTMLElement>('[data-particles-theme="dark"]')).map((zone) => {
      const rect = zone.getBoundingClientRect();
      return [rect.top, rect.bottom];
    });
  };

  const wrap = (particle: Particle): void => {
    if (particle.x < -10) particle.x = width + 10;
    else if (particle.x > width + 10) particle.x = -10;
    if (particle.y < -10) particle.y = height + 10;
    else if (particle.y > height + 10) particle.y = -10;
  };

  const createParticles = (): void => {
    const mobile = width < 768;
    const count = Math.max(18, Math.min(mobile ? 28 : 55, Math.round((width * height) / (mobile ? 24000 : 20000))));
    particles = Array.from({ length: count }, () => {
      const angle = Math.random() * Math.PI * 2;
      const speed = 0.08 + Math.random() * 0.17;
      return {
        x: Math.random() * width,
        y: Math.random() * height,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        radius: 0.8 + Math.random() * 1,
        tone: Math.floor(Math.random() * palettes.dark.length),
      };
    });
  };

  const draw = (): void => {
    measureZones();
    context.clearRect(0, 0, width, height);

    for (let i = 0; i < particles.length; i += 1) {
      const a = particles[i];
      for (let j = i + 1; j < particles.length; j += 1) {
        const b = particles[j];
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        if (distance > linkDistance) continue;
        const strength = 1 - distance / linkDistance;
        context.strokeStyle = isDark((a.y + b.y) / 2)
          ? `rgba(255, 255, 255, ${strength * 0.3})`
          : `rgba(3, 76, 176, ${strength * 0.1})`;
        context.lineWidth = 0.6;
        context.beginPath();
        context.moveTo(a.x, a.y);
        context.lineTo(b.x, b.y);
        context.stroke();
      }

      if (pointer.active) {
        const distance = Math.hypot(a.x - pointer.x, a.y - pointer.y);
        if (distance < pointerDistance) {
          const strength = 1 - distance / pointerDistance;
          context.strokeStyle = isDark(a.y)
            ? `rgba(0, 189, 247, ${strength * 0.48})`
            : `rgba(0, 75, 239, ${strength * 0.2})`;
          context.lineWidth = 0.7;
          context.beginPath();
          context.moveTo(a.x, a.y);
          context.lineTo(pointer.x, pointer.y);
          context.stroke();
        }
      }
    }

    particles.forEach((particle) => {
      const dark = isDark(particle.y);
      const color = (dark ? palettes.dark : palettes.light)[particle.tone];
      context.fillStyle = `rgba(${color}, ${dark ? 0.85 : 0.3})`;
      context.beginPath();
      context.arc(particle.x, particle.y, particle.radius * (dark ? 1.25 : 1), 0, Math.PI * 2);
      context.fill();
    });
  };

  const resize = (): void => {
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    // Sur mobile, la barre d'adresse change seulement la hauteur : on garde les particules.
    const widthChanged = window.innerWidth !== width;
    width = window.innerWidth;
    height = window.innerHeight;
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    if (widthChanged || !particles.length) createParticles();
    draw();
  };

  const step = (time: number): void => {
    const delta = lastTime ? Math.min((time - lastTime) / 16.67, 3) : 1;
    lastTime = time;

    particles.forEach((particle) => {
      particle.x += particle.vx * delta;
      particle.y += particle.vy * delta;
      wrap(particle);
    });

    draw();
    frame = requestAnimationFrame(step);
  };

  const start = (): void => {
    if (reducedMotion || frame || document.hidden) return;
    lastTime = 0;
    frame = requestAnimationFrame(step);
  };

  const stop = (): void => {
    cancelAnimationFrame(frame);
    frame = 0;
  };

  // Léger effet de profondeur : les particules accompagnent le défilement à 30 %.
  window.addEventListener(
    "scroll",
    () => {
      const shift = (window.scrollY - lastScroll) * 0.3;
      lastScroll = window.scrollY;
      particles.forEach((particle) => {
        particle.y -= shift;
        wrap(particle);
      });
      if (reducedMotion) draw();
    },
    { passive: true },
  );
  window.addEventListener("pointermove", (event) => {
    pointer.x = event.clientX;
    pointer.y = event.clientY;
    pointer.active = event.pointerType === "mouse";
    if (reducedMotion) draw();
  });
  document.documentElement.addEventListener("pointerleave", () => {
    pointer.active = false;
    if (reducedMotion) draw();
  });
  window.addEventListener("resize", resize);
  document.addEventListener("visibilitychange", () => (document.hidden ? stop() : start()));

  resize();
  start();
}

document.documentElement.classList.add("js");
renderIcons();
initPagePulse();
initMenu();
initNavDropdown();
initHeader();
initHeroSequence();
initScrollMotion();
initRevealAnimations();
initProductScrollReveal();
initMediaLibraryMotion();
initActiveNavigation();
initCounters();
initHeroControls();
initPageParticles();
initStoryDialog();
initBackToTop();
initCookieConsent();
initConsentEmbeds();
initPartnersCarousel();
initProjectsGallery();
initFormValidation();
initContactContext();
initMessagePulse();
initProductCatalog();
initProductGallery();
initAcademyFilters();
window.setTimeout(() => document.body.classList.add("ready"), prefersReducedMotion() ? 0 : 220);
